import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowDownIcon, CheckIcon, ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { cardClass, focusRing } from "../Exams/TestBuilder/ui";

// The anchors of the booklet page's cards, in the order a booklet is made (BtsDetail wraps each card in one).
export const STEP_ANCHORS = {
  settings: "bts-step-settings",
  subjects: "bts-step-subjects",
  check: "bts-step-check",
  activate: "bts-step-check",
  print: "bts-step-print",
};

// Brings a card into view (inside <main>, which scrolls) and its title to the keyboard.
const goTo = (step) => {
  const card = document.getElementById(STEP_ANCHORS[step]);
  if (!card) return;
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  card.scrollIntoView({ block: "start", behavior: reduce ? "auto" : "smooth" });
  const heading = card.querySelector("h2, h3");
  if (!heading) return;
  if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
  heading.focus({ preventScroll: true });
};

// Where a booklet stands on its way: each step done, the one to do now ("current"), one that found a problem
// ("problem"), or still ahead. A booklet is made in this order: its settings (made with it), a confirmed variant
// for each subject, a check without blocking problems, the activation (it freezes the content and the codes),
// then the PDFs and the answer keys.
export function bookletSteps(booklet) {
  const status = booklet.status;
  const draft = status === "draft";
  const subjects = booklet.check?.sections?.length || 0;
  const blocking = draft ? (booklet.check?.issues || []).filter((issue) => issue.blocking).length : 0;
  const documents = booklet.documents?.current || 0;
  const live = ["queued", "running"].includes(booklet.documents?.job?.status);
  const state = {
    settings: "done",
    subjects: subjects ? "done" : "current",
    check: !draft ? "done" : !subjects ? "ahead" : blocking ? "problem" : "done",
    activate: !draft ? "done" : subjects && !blocking ? "current" : "ahead",
    print: draft ? "ahead" : documents || status === "archived" ? "done" : "current",
  };
  const now = ["subjects", "check", "activate", "print"].find((step) => state[step] === "current" || state[step] === "problem") || null;
  return { state, now, subjects, blocking, documents, live, status };
}

const CIRCLE = {
  done: "bg-emerald-600 text-white",
  current: "bg-blue-600 text-white ring-4 ring-blue-600/15",
  problem: "bg-red-600 text-white ring-4 ring-red-600/15",
  ahead: "bg-white text-slate-400 ring-1 ring-inset ring-slate-300",
};

/**
 * The booklet page's map: the five steps of making a booklet, which are done, which one is now, and one line
 * saying what to do next with a way to the card it is done in.
 */
export default function BookletSteps({ booklet }) {
  const [t] = useTranslation();
  const { state, now, subjects, blocking, documents, live, status } = bookletSteps(booklet);
  const ORDER = ["settings", "subjects", "check", "activate", "print"];
  const detail = {
    settings: t("bts.steps.settings_detail"),
    subjects: subjects ? t("bts.steps.subjects_count", { count: subjects }) : t("bts.steps.subjects_none"),
    check: state.check === "problem" ? t("bts.steps.check_problems", { count: blocking }) : state.check === "done" ? t("bts.steps.check_ok") : "",
    activate: status === "draft" ? (state.activate === "current" ? t("bts.steps.activate_ready") : "") : t(`bts.steps.activate_${status}`),
    // A draft's PDFs are previews: the final ones come after the activation.
    print: status === "draft" ? t("bts.steps.print_later") : live ? t("bts.steps.print_running") : documents ? t("bts.steps.print_count", { count: documents }) : "",
  };

  // What to do now, with the button's own words.
  let next;
  if (now === "subjects") next = { step: "subjects", text: t("bts.steps.next_subjects", { save: t("bts.sections.save") }) };
  else if (now === "check") next = { step: "check", text: t("bts.steps.next_check", { count: blocking }) };
  else if (now === "activate") next = { step: "activate", text: t("bts.steps.next_activate", { activate: t("bts.actions.activate") }), noJump: true };
  else if (now === "print") next = { step: "print", text: live ? t("bts.steps.next_print_running") : t("bts.steps.next_print", { create: t("bts.print.create_final") }) };
  else next = { step: status === "archived" ? null : "print", text: status === "archived" ? t("bts.steps.next_archived") : t("bts.steps.next_done") };

  return (
    <nav aria-label={t("bts.steps.label")} className={`${cardClass} p-4 sm:p-5`}>
      <ol className="grid gap-3 sm:grid-cols-5 sm:gap-2">
        {ORDER.map((step, index) => {
          const s = state[step];
          const isNow = step === now;
          return (
            <li key={step} className="relative min-w-0">
              {/* The line to the next step (wide screens). */}
              {index < ORDER.length - 1 && (
                <span aria-hidden="true" className={`absolute left-[calc(50%+1.25rem)] right-[calc(-50%+1.25rem)] top-4 hidden h-px sm:block ${s === "done" ? "bg-emerald-300" : "bg-slate-200"}`} />
              )}
              <button
                type="button"
                onClick={() => goTo(step)}
                aria-current={isNow ? "step" : undefined}
                className={`flex w-full items-center gap-3 rounded-xl p-1 text-left transition duration-200 hover:bg-slate-50 sm:flex-col sm:gap-1.5 sm:text-center ${focusRing}`}
              >
                <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${CIRCLE[s]}`}>
                  {s === "done" ? (
                    <CheckIcon className="h-4 w-4" aria-hidden="true" />
                  ) : s === "problem" ? (
                    <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="min-w-0">
                  <span className={`block text-sm font-semibold ${s === "ahead" ? "text-slate-500" : "text-slate-900"}`}>
                    {t(`bts.steps.${step}`)}
                  </span>
                  {detail[step] && (
                    <span className={`block text-xs ${s === "problem" ? "font-medium text-red-700" : isNow ? "text-blue-700" : "text-slate-500"}`}>{detail[step]}</span>
                  )}
                  <span className="sr-only">
                    {" "}
                    ({t(`bts.steps.state_${s}`)})
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      <div
        className={`mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl px-3 py-2.5 text-sm ${
          now === "check" ? "bg-red-50 text-red-800" : now ? "bg-blue-50 text-blue-900" : "bg-slate-50 text-slate-700"
        }`}
      >
        <p className="min-w-0 flex-1">
          {status !== "archived" && <span className="font-semibold">{now ? t("bts.steps.now") : t("bts.steps.ready")} </span>}
          {next.text}
        </p>
        {next.step && !next.noJump && (
          <button
            type="button"
            onClick={() => goTo(next.step)}
            className={`inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 font-medium underline-offset-2 hover:underline max-sm:min-h-11 ${focusRing}`}
          >
            <ArrowDownIcon className="h-4 w-4" aria-hidden="true" />
            {t(`bts.steps.go_${next.step}`)}
          </button>
        )}
      </div>
    </nav>
  );
}
